import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Container,
  Paper,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Grid,
  IconButton,
  Tooltip,
  Collapse,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const FilterBar = ({ 
  onSearch, 
  onFilterChange, 
  onSortChange,
  onOpenNowChange, 
  currentFilter = 'all',
  currentSort = 'random',
  isOpenNow = false,
  userLocation = null,
  locationStatus = 'pending'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValue, setFilterValue] = useState(currentFilter);
  const [sortValue, setSortValue] = useState(currentSort);
  const [expanded, setExpanded] = useState(false);
  const [openNow, setOpenNow] = useState(isOpenNow);
  const [selectedHour, setSelectedHour] = useState('all');
  
  // Update sortOptions to include distance option with disabled state
  const sortOptions = [
    { value: 'random', label: 'Random' },
    { value: 'headline', label: 'Name' },
    { 
      value: 'distance', 
      label: 'Distance',
      disabled: locationStatus !== 'granted'
    }
  ];

  const hourOptions = [
    { value: 'all', label: 'Any Time' },
    { value: '12', label: '12:00' },
    { value: '13', label: '13:00' },
    { value: '14', label: '14:00' },
    { value: '15', label: '15:00' },
    { value: '16', label: '16:00' },
    { value: '17', label: '17:00' },
    { value: '18', label: '18:00' },
    { value: '19', label: '19:00' },
    { value: '20', label: '20:00' },
    { value: '21', label: '21:00' },
    { value: '22', label: '22:00' },
    { value: '23', label: '23:00' },
  ];

  // Update local state when props change
  useEffect(() => {
    setFilterValue(currentFilter);
  }, [currentFilter]);
  
  useEffect(() => {
    setSortValue(currentSort);
  }, [currentSort]);

  // Add this effect to handle sort value changes when location status changes
  useEffect(() => {
    if (locationStatus === 'denied' && sortValue === 'distance') {
      setSortValue('random');
      onSortChange('random');
    }
  }, [locationStatus, sortValue, onSortChange]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleOpenNowChange = (event) => {
    setOpenNow(event.target.checked);
    onOpenNowChange(event.target.checked);
  };

  const handleHourChange = (event) => {
    const value = event.target.value;
    setSelectedHour(value);
    onFilterChange({ day: filterValue, hour: value });
  };

  const handleDayFilterChange = (event) => {
    const value = event.target.value;
    setFilterValue(value);
    onFilterChange({ day: value, hour: selectedHour });
  };

  const handleSortChange = (event) => {
    const value = event.target.value;
    setSortValue(value);
    onSortChange(value);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Box
      component={Paper}
      elevation={3}
      sx={{
        position: 'sticky',
        top: { xs: 0, md: theme.spacing(1) },
        zIndex: 10,
        py: 2,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        mb: 3
      }}
    >
      <Container maxWidth="lg">
        {/* Mobile view - always shows search, toggle for filters */}
        {isMobile && (
          <>
            <Box sx={{ px: 1, mb: expanded ? 2 : 0 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search happy hours..."
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        onClick={handleClearSearch}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              <Button
                fullWidth
                size="small"
                onClick={toggleExpanded}
                endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mt: 1 }}
              >
                {expanded ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Box>
            
            <Collapse in={expanded}>
              <Grid container spacing={2} sx={{ px: 1 }}>
                {/* Add Open Now toggle as first item */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={openNow}
                          onChange={handleOpenNowChange}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Open Now"
                    />
                  </Box>
                </Grid>

                {/* Existing filters */}
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="day-filter-label">When?</InputLabel>
                    <Select
                      labelId="day-filter-label"
                      id="day-filter"
                      value={filterValue}
                      label="When?"
                      onChange={handleDayFilterChange}
                      startAdornment={
                        <InputAdornment position="start">
                          <FilterListIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="all">All Days</MenuItem>
                      <MenuItem value="sunday">Sunday</MenuItem>
                      <MenuItem value="monday">Monday</MenuItem>
                      <MenuItem value="tuesday">Tuesday</MenuItem>
                      <MenuItem value="wednesday">Wednesday</MenuItem>
                      <MenuItem value="thursday">Thursday</MenuItem>
                      <MenuItem value="friday">Friday</MenuItem>
                      <MenuItem value="saturday">Saturday</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Time</InputLabel>
                    <Select
                      value={selectedHour}
                      label="Time"
                      onChange={handleHourChange}
                      startAdornment={
                        <InputAdornment position="start">
                          <AccessTimeIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      {hourOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="sort-label">Sort By</InputLabel>
                    <Select
                      labelId="sort-label"
                      id="sort-by"
                      value={sortValue}
                      label="Sort By"
                      onChange={handleSortChange}
                      startAdornment={
                        <InputAdornment position="start">
                          <SortIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      {sortOptions.map((option) => (
                        <MenuItem 
                          key={option.value} 
                          value={option.value}
                          disabled={option.disabled}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </>
        )}
        
        {/* Desktop view - always shows all controls */}
        {!isMobile && (
          <Grid container spacing={3} alignItems="center">
            {/* Search Field */}
            <Grid item sm={3}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search happy hours..."
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear search"
                        onClick={handleClearSearch}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>

            {/* Open Now Switch */}
            <Grid item sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={openNow}
                    onChange={handleOpenNowChange}
                    color="primary"
                  />
                }
                label="Open Now"
              />
            </Grid>
            
            {/* Filter Dropdown */}
            <Grid item sm={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Day</InputLabel>
                <Select
                  value={filterValue}
                  label="Day"
                  onChange={handleDayFilterChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterListIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Days</MenuItem>
                  <MenuItem value="sunday">Sunday</MenuItem>
                  <MenuItem value="monday">Monday</MenuItem>
                  <MenuItem value="tuesday">Tuesday</MenuItem>
                  <MenuItem value="wednesday">Wednesday</MenuItem>
                  <MenuItem value="thursday">Thursday</MenuItem>
                  <MenuItem value="friday">Friday</MenuItem>
                  <MenuItem value="saturday">Saturday</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Time Dropdown */}
            <Grid item sm={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Time</InputLabel>
                <Select
                  value={selectedHour}
                  label="Time"
                  onChange={handleHourChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <AccessTimeIcon />
                    </InputAdornment>
                  }
                >
                  {hourOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Sort Dropdown */}
            <Grid item sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortValue}
                  label="Sort By"
                  onChange={handleSortChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <SortIcon />
                    </InputAdornment>
                  }
                >
                  {sortOptions.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default FilterBar;